import React, { useState, useEffect } from "react";
import { FaTrash, FaKey, FaTimes } from "react-icons/fa";
import AdminSideMenu from "../../components/AdminSideMenu";
import api from "../../utils/axiosInstance";

const AdminManagement = () => {
  const [roles, setRoles] = useState([]);
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adminsLoading, setAdminsLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showResetModal, setShowResetModal] = useState(false);
  const [selectedAdmin, setSelectedAdmin] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [activeTab, setActiveTab] = useState("");
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [resetPasswordSubmitting, setResetPasswordSubmitting] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [resetError, setResetError] = useState("");

  const [formData, setFormData] = useState({
    roleName: "",
    name: "",
    email: "",
    password: "",
  });

  // Function to format role name (MAIN_ADMIN -> Main Admin)
  const formatRoleName = (roleName) => {
    return roleName
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
  };

  // Fetch roles from backend on component mount
  useEffect(() => {
    const fetchRoles = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await api.get("/roles/all");

        if (response.data.success) {
          setRoles(response.data.roles);
          // Set first role as active tab by default
          if (response.data.roles.length > 0) {
            setActiveTab(response.data.roles[0].roleName);
          }
        }
      } catch (err) {
        console.error("Error fetching roles:", err);
        setError("Failed to fetch roles");
      } finally {
        setLoading(false);
      }
    };

    fetchRoles();
  }, []);

  // Fetch admins by role when activeTab changes
  useEffect(() => {
    if (!activeTab) return;

    const fetchAdminsByRole = async () => {
      try {
        setAdminsLoading(true);
        setError(null);
        const response = await api.get(
          `/admin-management/by-role/${activeTab}`
        );

        if (response.data.success) {
          // Transform the data to match our display format
          const transformedAdmins = response.data.admins.map((admin) => ({
            id: admin._id,
            name: admin.name || "N/A",
            email: admin.email || "N/A",
            type: formatRoleName(response.data.role.roleName),
            status: "Active",
            createdDate: new Date(admin.createdAt).toLocaleDateString(),
          }));
          setAdmins(transformedAdmins);
        }
      } catch (err) {
        console.error("Error fetching admins by role:", err);
        setError("Failed to fetch admins");
        setAdmins([]);
      } finally {
        setAdminsLoading(false);
      }
    };

    fetchAdminsByRole();
  }, [activeTab]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async () => {
    if (
      !formData.roleName ||
      !formData.name ||
      !formData.email ||
      !formData.password
    ) {
      alert("Please fill all required fields");
      return;
    }
    if (formData.password.length < 8) {
      alert("Password must be at least 8 characters");
      return;
    }

    try {
      setSubmitting(true);
      setError(null);

      // Prepare payload with roleName instead of role
      const payload = {
        name: formData.name,
        email: formData.email,
        password: formData.password,
        roleName: formData.roleName,
      };

      const response = await api.post("/admin-management/create", payload);

      if (response.data.success) {
        alert("Admin created successfully");
        setShowModal(false);
        setFormData({ roleName: "", name: "", email: "", password: "" });

        // Refresh admins list for current role
        if (activeTab) {
          const refreshResponse = await api.get(
            `/admin-management/by-role/${activeTab}`
          );
          if (refreshResponse.data.success) {
            const transformedAdmins = refreshResponse.data.admins.map(
              (admin) => ({
                id: admin._id,
                name: admin.name || "N/A",
                email: admin.email || "N/A",
                type: formatRoleName(refreshResponse.data.role.roleName),
                status: "Active",
                createdDate: new Date(admin.createdAt).toLocaleDateString(),
              })
            );
            setAdmins(transformedAdmins);
          }
        }
      }
    } catch (err) {
      console.error("Error creating admin:", err);
      setError(err.response?.data?.message || "Failed to create admin");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this admin?")) {
      try {
        const response = await api.delete(`/admin-management/delete/${id}`);

        if (response.data.success) {
          alert("Admin deleted successfully");
          // Remove from local state
          setAdmins(admins.filter((admin) => admin.id !== id));
        }
      } catch (err) {
        console.error("Error deleting admin:", err);
        alert(err.response?.data?.message || "Failed to delete admin");
      }
    }
  };

  const handleResetPassword = (admin) => {
    setSelectedAdmin(admin);
    setNewPassword("");
    setResetError("");
    setShowResetModal(true);
  };

  const confirmResetPassword = async () => {
    // Validation
    if (!newPassword) {
      setResetError("Please enter a new password");
      return;
    }

    if (newPassword.length < 8) {
      setResetError("Password must be at least 8 characters");
      return;
    }

    try {
      setResetPasswordSubmitting(true);
      setResetError("");

      const payload = {
        newPassword: newPassword,
      };

      const response = await api.put(
        `/admin-management/reset-password/${selectedAdmin.id}`,
        payload
      );

      if (response.data.success) {
        alert(`Password reset successfully for ${selectedAdmin.email}`);
        setShowResetModal(false);
        setSelectedAdmin(null);
        setNewPassword("");
        setResetError("");
      }
    } catch (err) {
      console.error("Error resetting password:", err);
      setResetError(err.response?.data?.message || "Failed to reset password");
    } finally {
      setResetPasswordSubmitting(false);
    }
  };

  const filteredAdmins = admins.filter((admin) => {
    const matchesSearch =
      admin.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      admin.email.toLowerCase().includes(searchTerm.toLowerCase());

    return matchesSearch;
  });

  const getAdminTypeColor = (type) => {
    const baseType = type.toUpperCase().replace(/ /g, "_");
    switch (baseType) {
      case "MARKETING_ADMIN":
        return "bg-purple-100 text-purple-800";
      case "ACCOUNTS_ADMIN":
        return "bg-green-100 text-green-800";
      case "MAIN_ADMIN":
      case "SUPER_ADMIN":
        return "bg-blue-100 text-blue-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col md:flex-row">
        <AdminSideMenu />
        <div className="md:ml-64 p-4 md:p-8 w-full bg-gray-50 min-h-screen flex items-center justify-center">
          <div className="text-gray-600 text-lg">Loading roles...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col md:flex-row">
      <AdminSideMenu />
      <div className="md:ml-64 p-4 md:p-8 w-full bg-gray-50 min-h-screen">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-800">Admins</h1>
          <p className="text-gray-600">Manage your admin users here</p>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-4 p-4 bg-red-100 text-red-800 rounded-lg">
            {error}
          </div>
        )}

        {/* Tabs - Dynamic from backend */}
        <div className="mb-6 border-b border-gray-200">
          <div className="flex gap-2 overflow-x-auto">
            {roles.map((role) => (
              <button
                key={role._id}
                onClick={() => setActiveTab(role.roleName)}
                className={`px-6 py-3 font-medium transition-colors whitespace-nowrap ${
                  activeTab === role.roleName
                    ? "border-b-2 border-blue-600 text-blue-600"
                    : "text-gray-600 hover:text-gray-800"
                }`}
              >
                {formatRoleName(role.roleName)}
              </button>
            ))}
          </div>
        </div>

        {/* Search and Add Button */}
        <div className="flex flex-col md:flex-row justify-between items-center mb-6 gap-4">
          <input
            type="text"
            placeholder="Search by Name or Email"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full md:w-96 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <div className="flex items-center gap-4">
            <span className="text-gray-600 font-medium">
              Showing {filteredAdmins.length}{" "}
              {adminsLoading ? "(loading...)" : ""}
            </span>
            <button
              onClick={() => {
                setFormData((prev) => ({ ...prev, roleName: activeTab }));
                setShowModal(true);
              }}
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg font-medium transition-colors"
            >
              Add Admin
            </button>
          </div>
        </div>

        {/* Admin Grid */}
        {adminsLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="text-gray-600 text-lg">Loading admins...</div>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredAdmins.map((admin) => (
                <div
                  key={admin.id}
                  className="bg-white rounded-lg shadow-md border border-gray-200 p-6 hover:shadow-lg transition-shadow"
                >
                  {/* Admin Type Badge */}
                  <div className="flex justify-between items-start mb-4">
                    <span
                      className={`px-3 py-1 rounded-full text-xs font-semibold ${getAdminTypeColor(
                        admin.type
                      )}`}
                    >
                      {admin.type}
                    </span>
                    <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full font-medium">
                      {admin.status}
                    </span>
                  </div>

                  {/* Admin Info */}
                  <h3 className="text-xl font-bold text-gray-800 mb-2">
                    {admin.name}
                  </h3>
                  <div className="space-y-2 text-sm text-gray-600 mb-4">
                    <p>
                      <span className="font-medium">Email:</span> {admin.email}
                    </p>
                    <p>
                      <span className="font-medium">Created:</span>{" "}
                      {admin.createdDate}
                    </p>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex gap-2 pt-4 border-t border-gray-200">
                    <button
                      onClick={() => handleResetPassword(admin)}
                      className="flex-1 flex items-center justify-center gap-2 bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg transition-colors text-sm font-medium"
                    >
                      <FaKey className="text-sm" />
                      Reset Password
                    </button>
                    <button
                      onClick={() => handleDelete(admin.id)}
                      className="flex items-center justify-center bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-lg transition-colors"
                    >
                      <FaTrash />
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {filteredAdmins.length === 0 && (
              <div className="text-center py-12">
                <p className="text-gray-500 text-lg">
                  No admins found for this role
                </p>
              </div>
            )}
          </>
        )}

        {/* Add Admin Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
              <div className="flex justify-between items-center p-6 border-b border-gray-200">
                <h2 className="text-2xl font-bold text-gray-800">
                  Add New Admin
                </h2>
                <button
                  onClick={() => setShowModal(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <FaTimes className="text-xl" />
                </button>
              </div>

              <div className="p-6">
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Admin Role *
                    </label>
                    <select
                      name="roleName"
                      value={formData.roleName}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Select Admin Role</option>
                      {roles.map((role) => (
                        <option key={role._id} value={role.roleName}>
                          {formatRoleName(role.roleName)}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Name *
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      placeholder="Enter admin name"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Email *
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder="Enter email address"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Password *
                    </label>
                    <input
                      type="password"
                      name="password"
                      value={formData.password}
                      onChange={handleInputChange}
                      placeholder="Enter password"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Minimum 8 characters
                    </p>
                  </div>
                </div>

                <div className="flex gap-3 mt-6">
                  <button
                    onClick={() => setShowModal(false)}
                    disabled={submitting}
                    className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSubmit}
                    disabled={submitting}
                    className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium disabled:opacity-50"
                  >
                    {submitting ? "Creating..." : "Create Admin"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Reset Password Modal */}
        {showResetModal && selectedAdmin && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
              <div className="text-center mb-6">
                <div className="bg-orange-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <FaKey className="text-3xl text-orange-600" />
                </div>
                <h2 className="text-2xl font-bold text-gray-800 mb-2">
                  Reset Password
                </h2>
                <p className="text-gray-600">
                  Reset password for{" "}
                  <span className="font-semibold">{selectedAdmin.name}</span>
                </p>
                <p className="text-sm text-gray-500 mt-2">
                  Email: {selectedAdmin.email}
                </p>
              </div>

              {/* Error Message */}
              {resetError && (
                <div className="mb-4 p-3 bg-red-100 text-red-800 rounded-lg text-sm">
                  {resetError}
                </div>
              )}

              {/* Password Input */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  New Password *
                </label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => {
                    setNewPassword(e.target.value);
                    setResetError("");
                  }}
                  placeholder="Enter new password"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Minimum 8 characters
                </p>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowResetModal(false);
                    setSelectedAdmin(null);
                    setNewPassword("");
                    setResetError("");
                  }}
                  disabled={resetPasswordSubmitting}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmResetPassword}
                  disabled={resetPasswordSubmitting}
                  className="flex-1 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors font-medium disabled:opacity-50"
                >
                  {resetPasswordSubmitting ? "Resetting..." : "Reset Password"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminManagement;
